import React, { useState, useEffect, useContext } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Stepper, Step } from '@mui/material';

import { AuthContext } from '../../commons/auth/AuthContext';
import {
    fetchInvoiceTrackingDetails,
    logInvoicingUserAgentInfo,
} from '../invoicingRoutes';
import { formatDateString, ENGLISH_FORMAT } from '../../commons/DateUtils';
import LoadingPage from '../../commons/LoadingPage';
import { CURRENCY } from '../../commons/Constants';
import { calculateTotalInvoiceCost } from '../form/InvoiceFormHelpers';
import { GenericMessagePage } from '../../commons/GenericMessagePage';
import {
    getInvoicingHeaderMessageElement,
    getInvoicingDetailsHeaderSubMessageElement,
} from '../../commons/MessageComponents';
import { getPlatformInfo } from '../../commons/UserAgentInfo';
import {
    getPaymentModeDisplay,
    ConsultantInvoicePaymentStatus,
} from '../InvoicingConstants';
import {
    CustomStepLabel,
    CustomConnector,
    CustomStepIcon,
} from '../../commons/stepper/StepperCustomComponents';
import ErrorPage from '../../commons/ErrorPage';
import WarningMessagePage from '../../commons/WarningMessagePage';
import {
    getInvoiceTrackingStepperDetails,
    getStepDateDetails,
} from './InvoiceTrackingPageHelpers';

const theme = createTheme();
const InvoiceTrackingPage = () => {
    const [invoiceData, setInvoiceData] = useState();

    const authContext = useContext(AuthContext);
    const routeMatch = useRouteMatch();
    const { url } = routeMatch.params;

    // execute effect on first render.
    useEffect(() => {
        fetchInvoiceTrackingDetails(url, authContext, setInvoiceData);
        logInvoicingUserAgentInfo(
            url,
            getPlatformInfo('app.invoiceTrackingPage'),
            authContext
        ).catch(console.error);
    }, []);

    const _getBillingRate = () => {
        const { billingRate, currency } = invoiceData;
        const currencySymbol = CURRENCY[currency].symbol;
        return `${billingRate} ${currencySymbol}/hr`;
    };

    const _getTotalCost = () => {
        const { billingRate, callDuration, currency } = invoiceData;
        const currencySymbol = CURRENCY[currency].symbol;
        const totalCost = calculateTotalInvoiceCost(billingRate, callDuration);
        return `${totalCost} ${currencySymbol}`;
    };

    const _renderInvoiceDetailsRow = (label, value) => (
        <tr>
            <td className="px-4 py-2 border border-gray-600 text-center font-semibold text-gray-800">
                {label}
            </td>
            <td className="px-4 py-2 border border-gray-600 shadow-inner text-center bg-gray-200 font-semibold text-blue-800">
                {value}
            </td>
        </tr>
    );

    const invoiceSubmissionDate = formatDateString(
        invoiceData?.invoiceSubmissionDate,
        ENGLISH_FORMAT
    );

    const paymentStatusUpdatedOn = invoiceData?.paymentStatusUpdatedOn;

    const _renderInvoiceDetailsTable = () => (
        <table className="table-fixed w-full">
            <tbody>
                {_renderInvoiceDetailsRow(
                    'Consultant Name',
                    invoiceData.consultantName
                )}
                {_renderInvoiceDetailsRow(
                    'Date of Consultation (in ET)',
                    formatDateString(
                        invoiceData.dateOfConsultation,
                        ENGLISH_FORMAT
                    )
                )}
                {_renderInvoiceDetailsRow(
                    'Invoice Submission Date',
                    invoiceSubmissionDate
                )}
                {_renderInvoiceDetailsRow('Billing Rate', _getBillingRate())}
                {_renderInvoiceDetailsRow(
                    'Call Duration',
                    `${invoiceData.callDuration} min`
                )}
                {_renderInvoiceDetailsRow('Total Cost', _getTotalCost())}
                {_renderInvoiceDetailsRow(
                    'Payment Method',
                    getPaymentModeDisplay(invoiceData.paymentMethod)
                )}
            </tbody>
        </table>
    );

    if (!invoiceData) return <LoadingPage />;

    const { paymentStatus, paymentMethod } = invoiceData;
    const { steps, activeStep } =
        getInvoiceTrackingStepperDetails(paymentStatus);

    const _renderInvoiceDetailsHeader = () => {
        if (!paymentStatus) {
            return (
                <div className="text-center">
                    <WarningMessagePage
                        message={getInvoicingHeaderMessageElement(
                            paymentStatus
                        )}
                        subMessage={getInvoicingDetailsHeaderSubMessageElement(
                            paymentMethod,
                            paymentStatus,
                            invoiceSubmissionDate
                        )}
                    />
                </div>
            );
        }
        if (paymentStatus === ConsultantInvoicePaymentStatus.PAYMENT_FAILED) {
            return (
                <div className="text-center">
                    <ErrorPage
                        message={getInvoicingHeaderMessageElement(
                            paymentStatus
                        )}
                        subMessage={getInvoicingDetailsHeaderSubMessageElement(
                            paymentMethod,
                            paymentStatus,
                            invoiceSubmissionDate
                        )}
                    />
                </div>
            );
        }

        return (
            <GenericMessagePage
                message={getInvoicingHeaderMessageElement(paymentStatus)}
                subMessage={getInvoicingDetailsHeaderSubMessageElement(
                    paymentMethod,
                    paymentStatus,
                    invoiceSubmissionDate
                )}
            />
        );
    };

    return (
        <ThemeProvider theme={theme}>
            {_renderInvoiceDetailsHeader()}
            <div className="max-w-3xl mx-auto bg-white w-full px-6 py-8 md:mt-6 md:mb-6 rounded shadow-md text-md md:text-sm">
                <div className="w-full text-xl text-center py-2 font-semibold text-blue-700 leading-relaxed mb-2">
                    Your Invoice Details
                </div>
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    connector={<CustomConnector />}
                >
                    {steps.map((step, index) => {
                        const { dateLabel, isEstimatedDate } =
                            getStepDateDetails(
                                index,
                                step.status,
                                paymentStatusUpdatedOn,
                                invoiceData?.invoiceSubmissionDate
                            );
                        return (
                            <Step key={step.label}>
                                <CustomStepLabel
                                    statusLabel={step.label}
                                    StepIconComponent={(props) => (
                                        <CustomStepIcon
                                            {...props}
                                            status={step.status}
                                        />
                                    )}
                                    status={step.status}
                                    dateLabel={dateLabel}
                                    isEstimatedDate={isEstimatedDate}
                                    paymentMethod={paymentMethod}
                                />
                            </Step>
                        );
                    })}
                </Stepper>
                <div
                    style={{ marginTop: 20 }}
                    className="max-w-lg mx-auto mt-2 pb-6 w-full md:w-4/5"
                >
                    {invoiceData && _renderInvoiceDetailsTable()}
                </div>
            </div>
        </ThemeProvider>
    );
};

export default InvoiceTrackingPage;
